/**
 * Hook for reconnecting to live WebSocket streams for in-progress jobs.
 *
 * When the user navigates away and returns (or refreshes), any jobs that were
 * streaming via WebSocket are now disconnected. This hook detects in-progress
 * jobs and attempts to re-subscribe to their live WS streams by sending a
 * `subscribe_job` message. If the server doesn't support it or the connection
 * fails, it signals the caller to fall back to CloudWatch polling.
 *
 * Flow:
 *   1. Called with a jobId to reconnect to
 *   2. Opens a new WS, waits for 'connected' handshake
 *   3. Sends { type: 'subscribe_job', jobId, userId, stage }
 *   4. On 'subscribed' confirmation → pipes messages through existing handlers
 *   5. On 'subscribe_error' or WS failure → calls onFallbackToCloudWatch
 *   6. On job completion/failure/cancel → cleans up
 */
import { useCallback, useRef } from 'react';
import { AgentLogEntry, JobRun, JobStage, JobStatus } from '../types';
import { WEBSOCKET_URL, getWebSocketStage } from '../config';
import {
  classifyLogLevel,
  handleStageUpdateMessage,
  handleAgentTextMessage,
  handleAgentToolStartMessage,
  handleLogMessage,
  handleJobCompletedMessage,
  handleJobFailedMessage,
  handleJobCancelledMessage,
  handleProgressMessage,
  handleArtifactCreatedMessage,
  handleJobBreakdownMessage,
  handleErrorMessage,
  handleDefaultMessage,
  handleMainWsClose,
  MessageHandlerDeps
} from '../utils/wsMessageHandlers';
import { PersistedEphemeralEntry } from './useAgentPanel';

const RECONNECT_TIMEOUT_MS = 5000;

export interface ReconnectDeps {
  userAlias: string;
  wsMapRef: React.MutableRefObject<Map<string, WebSocket>>;
  addLog: (level: AgentLogEntry['level'], message: string, stage?: string, pipelineId?: string) => void;
  addPipelineLog: (pipelineId: string, level: AgentLogEntry['level'], message: string, stage?: string) => void;
  setIsAgentPanelVisible: (visible: boolean) => void;
  setJobStatus: (status: JobStatus) => void;
  setProgressMessage: (msg: string) => void;
  setStreamingText: React.Dispatch<React.SetStateAction<string>>;
  streamingTextRef: React.MutableRefObject<string>;
  setPersistedEphemeralEntries: React.Dispatch<React.SetStateAction<PersistedEphemeralEntry[]>>;
  setPipelinePersistedEphemeral: React.Dispatch<React.SetStateAction<Record<string, PersistedEphemeralEntry[]>>>;
  setPipelineStreamingText: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  pipelineStreamingTextRef: React.MutableRefObject<Record<string, string>>;
  setParallelPipelines: React.Dispatch<React.SetStateAction<string[]>>;
  setPipelineLogs: React.Dispatch<React.SetStateAction<Record<string, AgentLogEntry[]>>>;
  setActivePipelineTab: (tab: string) => void;
  setJobHistory: React.Dispatch<React.SetStateAction<JobRun[]>>;
  setCurrentJobId: (id: string | null) => void;
  currentJobIdRef: React.MutableRefObject<string | null>;
  isJobRunningRef: React.MutableRefObject<boolean>;
  jobHistory: JobRun[];
  selectedLambdas: never[];
  selectedVersions: Record<string, never>;
  accounts: never[];
  currentConfigName: string;
  getSelectedPipelines: () => string[];
  parallelPipelines: string[];
}

/**
 * Builds the MessageHandlerDeps subset from ReconnectDeps.
 * selectedLambdas/selectedVersions/accounts are empty since we're just subscribing,
 * not starting a new job.
 */
const toHandlerDeps = (deps: ReconnectDeps): MessageHandlerDeps => ({
  userAlias: deps.userAlias,
  selectedLambdas: deps.selectedLambdas,
  selectedVersions: deps.selectedVersions,
  accounts: deps.accounts,
  currentConfigName: deps.currentConfigName,
  getSelectedPipelines: deps.getSelectedPipelines,
  addLog: deps.addLog,
  addPipelineLog: deps.addPipelineLog,
  setIsAgentPanelVisible: deps.setIsAgentPanelVisible,
  setJobStatus: deps.setJobStatus,
  setProgressMessage: deps.setProgressMessage,
  setStreamingText: deps.setStreamingText,
  streamingTextRef: deps.streamingTextRef,
  setPersistedEphemeralEntries: deps.setPersistedEphemeralEntries,
  setPipelinePersistedEphemeral: deps.setPipelinePersistedEphemeral,
  setPipelineStreamingText: deps.setPipelineStreamingText,
  pipelineStreamingTextRef: deps.pipelineStreamingTextRef,
  setParallelPipelines: deps.setParallelPipelines,
  setPipelineLogs: deps.setPipelineLogs,
  setActivePipelineTab: deps.setActivePipelineTab,
  setJobHistory: deps.setJobHistory,
  setCurrentJobId: deps.setCurrentJobId,
  currentJobIdRef: deps.currentJobIdRef,
  isJobRunningRef: deps.isJobRunningRef,
  jobHistory: deps.jobHistory
});

export const useWebSocketReconnect = (deps: ReconnectDeps) => {
  const depsRef = useRef<ReconnectDeps>(deps);
  depsRef.current = deps;

  // Track active reconnect WS connections by jobId
  const reconnectWsMap = useRef<Map<string, WebSocket>>(new Map());

  /**
   * Attempt to subscribe to a running job's live WS stream.
   * Returns a promise that resolves to:
   *   - 'subscribed' if successfully attached to live stream
   *   - 'fallback' if server can't provide live stream (use CloudWatch)
   */
  const subscribeToJob = useCallback((jobId: string): Promise<'subscribed' | 'fallback'> => {
    return new Promise((resolve) => {
      const {
        userAlias,
        wsMapRef,
        addLog,
        setJobStatus,
        setProgressMessage,
        setIsAgentPanelVisible,
        isJobRunningRef,
        setCurrentJobId,
        currentJobIdRef
      } = depsRef.current;

      // Already have an active WS for this job
      const existingWs = wsMapRef.current.get(jobId);
      if (existingWs && existingWs.readyState === WebSocket.OPEN) {
        resolve('subscribed');
        return;
      }

      // Already have a reconnect WS for this job
      const existingReconnect = reconnectWsMap.current.get(jobId);
      if (existingReconnect && existingReconnect.readyState === WebSocket.OPEN) {
        resolve('subscribed');
        return;
      }

      console.log(`[Upgrade Medic] Attempting WS reconnect for job: ${jobId}`);

      try {
        const ws = new WebSocket(WEBSOCKET_URL);

        const timeout = setTimeout(() => {
          console.log(`[Upgrade Medic] WS reconnect timeout for job: ${jobId}`);
          ws.close();
          resolve('fallback');
        }, RECONNECT_TIMEOUT_MS);

        ws.onopen = () => {
          console.log(`[Upgrade Medic] WS reconnect connection opened for job: ${jobId}`);
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            const latestDeps = toHandlerDeps(depsRef.current);

            if (message.type === 'connected') {
              // Send subscribe_job after handshake
              const subscribePayload = {
                type: 'subscribe_job',
                jobId,
                userId: userAlias,
                stage: getWebSocketStage()
              };
              ws.send(JSON.stringify(subscribePayload));
              return;
            }

            if (message.type === 'subscribed') {
              clearTimeout(timeout);
              console.log(`[Upgrade Medic] Successfully subscribed to live stream for job: ${jobId}`);

              // Register in maps
              reconnectWsMap.current.set(jobId, ws);
              wsMapRef.current.set(jobId, ws);

              // Set up live streaming state
              isJobRunningRef.current = true;
              setCurrentJobId(jobId);
              currentJobIdRef.current = jobId;
              setJobStatus('running');
              setIsAgentPanelVisible(true);
              setProgressMessage(`Reconnected to live stream for ${jobId}`);
              addLog('phase', `🔄 Reconnected to live stream for job ${jobId}`);

              if (message.currentStage) {
                addLog('info', `Current stage: ${message.currentStage} (${message.progress ?? 0}%)`);
              }

              resolve('subscribed');
              return;
            }

            if (message.type === 'subscribe_error') {
              clearTimeout(timeout);
              console.log(`[Upgrade Medic] Subscribe error for job ${jobId}: ${message.error}`);
              ws.close();
              resolve('fallback');
              return;
            }

            // All other message types — pipe through existing handlers
            console.log(`[Upgrade Medic][Reconnect] Received message type: ${message.type}`, message);
            const currentDeps = depsRef.current;

            try {
              switch (message.type) {
                case 'progress':
                  handleProgressMessage(message, latestDeps);
                  break;
                case 'job_breakdown':
                  handleJobBreakdownMessage(message, latestDeps);
                  break;
                case 'pipeline_started':
                  currentDeps.addPipelineLog(message.pipelineId, 'phase', '▶️ Pipeline started');
                  break;
                case 'pipeline_completed':
                  currentDeps.addPipelineLog(message.pipelineId, 'phase', '✅ Pipeline completed successfully');
                  break;
                case 'pipeline_failed':
                  currentDeps.addPipelineLog(message.pipelineId, 'error', `❌ Pipeline failed: ${message.error || 'Unknown error'}`);
                  break;
                case 'agent_text':
                  handleAgentTextMessage(message, latestDeps);
                  break;
                case 'agent_tool_start':
                  handleAgentToolStartMessage(message, currentDeps.parallelPipelines, latestDeps);
                  break;
                case 'agent_tool_result':
                  break;
                case 'agent_complete':
                  currentDeps.setStreamingText('');
                  if (message.pipelineId) {
                    currentDeps.addPipelineLog(
                      message.pipelineId,
                      'phase',
                      `✅ Agent completed: ${message.stats?.tool_calls || 0} tool calls, ${message.stats?.cycles || 0} cycles`
                    );
                  } else {
                    currentDeps.addLog(
                      'phase',
                      `✅ Agent completed: ${message.stats?.tool_calls || 0} tool calls, ${message.stats?.cycles || 0} cycles`
                    );
                  }
                  break;
                case 'stage_update':
                  handleStageUpdateMessage(message, latestDeps);
                  break;
                case 'artifact_created':
                  handleArtifactCreatedMessage(message, latestDeps);
                  break;
                case 'stage_start':
                  if (message.pipelineId) {
                    currentDeps.addPipelineLog(message.pipelineId, 'phase', `Stage: ${message.stage} - ${message.message || 'Starting...'}`);
                  } else {
                    currentDeps.addLog('phase', `Stage: ${message.stage} - ${message.message || 'Starting...'}`);
                  }
                  currentDeps.setProgressMessage(`Stage: ${message.stage}`);
                  break;
                case 'stage_end':
                  if (message.pipelineId) {
                    if (message.success) {
                      currentDeps.addPipelineLog(message.pipelineId, 'phase', `✅ Stage ${message.stage}: Completed`);
                    } else {
                      currentDeps.addPipelineLog(
                        message.pipelineId,
                        'error',
                        `❌ Stage ${message.stage}: Failed - ${message.error || 'Unknown error'}`
                      );
                    }
                  } else {
                    if (message.success) {
                      currentDeps.addLog('phase', `✅ Stage ${message.stage}: Completed`);
                    } else {
                      currentDeps.addLog('error', `❌ Stage ${message.stage}: Failed - ${message.error || 'Unknown error'}`);
                    }
                  }
                  break;
                case 'log':
                  handleLogMessage(message, latestDeps);
                  break;
                case 'output':
                  if (message.pipelineId) {
                    currentDeps.addPipelineLog(message.pipelineId, 'agent', message.data || message.message || JSON.stringify(message));
                  } else {
                    currentDeps.addLog('agent', message.data || message.message || JSON.stringify(message));
                  }
                  break;
                case 'job_completed':
                  handleJobCompletedMessage(message, latestDeps);
                  cleanupReconnect(jobId);
                  break;
                case 'job_failed':
                  handleJobFailedMessage(message, latestDeps);
                  cleanupReconnect(jobId);
                  break;
                case 'job_cancelled':
                  handleJobCancelledMessage(message, latestDeps);
                  cleanupReconnect(jobId);
                  break;
                case 'error':
                  handleErrorMessage(message, latestDeps);
                  break;
                case 'pong':
                  break;
                default:
                  handleDefaultMessage(message, latestDeps);
              }
            } catch (handlerError) {
              console.error(`[Upgrade Medic][Reconnect] Handler error for message type ${message.type}:`, handlerError);
              // Always surface the message in the UI even if the handler throws
              const fallbackText = message.message || message.data || JSON.stringify(message);
              currentDeps.addLog(classifyLogLevel(fallbackText), fallbackText);
            }
          } catch {
            const rawData = event.data;
            depsRef.current.addLog(classifyLogLevel(rawData), rawData);
          }
        };

        ws.onerror = () => {
          clearTimeout(timeout);
          console.log(`[Upgrade Medic] WS reconnect error for job: ${jobId}`);
          resolve('fallback');
        };

        ws.onclose = () => {
          clearTimeout(timeout);
          reconnectWsMap.current.delete(jobId);
          depsRef.current.wsMapRef.current.delete(jobId);

          // If the job was still running when the WS closed, mark it as failed
          // (mirrors handleMainWsClose behavior for the primary WS)
          const { isJobRunningRef, currentJobIdRef } = depsRef.current;
          if (isJobRunningRef.current && currentJobIdRef.current === jobId) {
            handleMainWsClose(toHandlerDeps(depsRef.current));
          }
        };
      } catch {
        console.error(`[Upgrade Medic] Failed to create WS for reconnect to job: ${jobId}`);
        resolve('fallback');
      }
    });
  }, []);

  /** Clean up a reconnect WS for a specific job. */
  const cleanupReconnect = useCallback((jobId: string) => {
    const ws = reconnectWsMap.current.get(jobId);
    if (ws) {
      ws.close();
      reconnectWsMap.current.delete(jobId);
    }
  }, []);

  /** Clean up all reconnect WS connections (for unmount). */
  const cleanupAllReconnects = useCallback(() => {
    reconnectWsMap.current.forEach((ws) => ws.close());
    reconnectWsMap.current.clear();
  }, []);

  /** Check if a job has an active reconnect WS. */
  const hasActiveReconnect = useCallback((jobId: string): boolean => {
    const ws = reconnectWsMap.current.get(jobId);
    return !!ws && ws.readyState === WebSocket.OPEN;
  }, []);

  return {
    subscribeToJob,
    cleanupReconnect,
    cleanupAllReconnects,
    hasActiveReconnect
  };
};
